import type { MenuScreen, MenuContext } from '../types.js';
import { createScreenShell, createMenuHeader, createCrestButton, createCopyButton, createInviteFriendButton, createSidePicker, el, withFocusPreserved } from '@bn-games/ui-shell';
import { ROOM_CODE_LENGTH } from '@bn-games/network-protocol';
import { t } from '../../i18n/I18n.js';
import { parseVariant } from '../../network/protocol.js';
import type { Color } from '../../game/types.js';

function shareLinkFor(code: string): string {
  return `${window.location.origin}${window.location.pathname}#room=${code}`;
}

function formatRoomCode(code: string | null): string {
  if (!code) return '·'.repeat(ROOM_CODE_LENGTH);
  return code.split('').join(' ');
}

/**
 * Two phases on the same screen:
 * 1. Pre-create — show the side picker, let the host pick their color, click
 *    Create to actually open the room.
 * 2. Post-create — show the room code + waiting status (the original screen).
 */
export function createOnlineCreateScreen(): MenuScreen {
  let frame: HTMLElement | null = null;
  let root: HTMLElement | null = null;
  let unsubRoom: (() => void) | null = null;
  let created = false;

  function paint(ctx: MenuContext): void {
    if (!root) return;
    while (root.firstChild) root.removeChild(root.firstChild);
    const shell = createScreenShell();
    frame = shell.frame;
    const body = shell.body;

    body.appendChild(createMenuHeader({ title: t('online.create.title') }));

    if (!created) {
      const chosen: Color = ctx.getPendingOnlineColor();
      const sidePicker = createSidePicker<Color>({
        label: t('preGame.sideLabel'),
        leftValue: 'white',
        leftLabel: t('common.white'),
        rightValue: 'black',
        rightLabel: t('common.black'),
        value: chosen,
        onChange: (next) => {
          ctx.setPendingOnlineColor(next);
          withFocusPreserved(() => paint(ctx), root ?? undefined);
        },
      });

      const createBtn = createCrestButton({
        label: t('online.create.create'),
        primary: true,
        onClick: () => {
          created = true;
          ctx.setOpponent('online');
          ctx.createRoom();
          paint(ctx);
        },
      });

      const center = el('div', { class: 'about-center' }, [
        sidePicker,
        el('div', { class: 'button-stack' }, [createBtn]),
      ]);

      const back = el('div', { class: 'button-row back-button-row' }, [
        createCrestButton({
          label: t('common.back'),
          small: true,
          onClick: () => ctx.navigate('play'),
        }),
      ]);

      body.appendChild(center);
      body.appendChild(back);
      root.appendChild(frame);
      return;
    }

    const info = ctx.roomClient.getInfo();
    const center = el('div', { class: 'online-center' });
    const label = el('div', { class: 'online-label', text: t('online.create.codeLabel') });
    const codeNode = el('div', { class: 'online-code', text: formatRoomCode(info.roomCode) });
    const share = el('div', { class: 'online-hint', text: t('online.create.share') });
    const variant = parseVariant(info.variant ?? undefined);
    const variantNode = el('div', {
      class: 'online-hint',
      text: t('online.create.variantLabel', { variant: t(`settings.variants.${variant}`) }),
    });
    const statusNode = el('div', { class: 'online-status waiting-dots' });

    switch (info.state) {
      case 'connecting':
        statusNode.textContent = t('online.status.connecting');
        statusNode.classList.add('waiting-dots');
        break;
      case 'creating':
        statusNode.textContent = t('online.status.creating');
        statusNode.classList.add('waiting-dots');
        break;
      case 'waiting_for_opponent':
        statusNode.textContent = t('online.status.waitingForOpponent');
        statusNode.classList.add('waiting-dots');
        break;
      case 'ready':
        statusNode.textContent = t('online.status.opponentJoined');
        statusNode.classList.remove('waiting-dots');
        break;
      case 'error':
        statusNode.textContent = info.errorMessage ?? t('online.status.error');
        statusNode.classList.remove('waiting-dots');
        break;
      default:
        statusNode.textContent = info.state;
        statusNode.classList.remove('waiting-dots');
    }

    const roomCode = info.roomCode;
    const shareRow = roomCode
      ? el('div', { class: 'button-row' }, [
          createCopyButton({
            label: t('online.share.copyCode'),
            copiedLabel: t('online.share.copied'),
            getText: () => roomCode,
          }),
          createCopyButton({
            label: t('online.share.copyLink'),
            copiedLabel: t('online.share.copied'),
            getText: () => shareLinkFor(roomCode),
          }),
        ])
      : null;

    const inviteBtn = roomCode ? createInviteFriendButton({ game: 'checkers', roomCode }) : null;
    const inviteRow = inviteBtn ? el('div', { class: 'button-row' }, [inviteBtn]) : null;

    center.appendChild(label);
    center.appendChild(codeNode);
    if (shareRow) center.appendChild(shareRow);
    if (inviteRow) center.appendChild(inviteRow);
    center.appendChild(share);
    center.appendChild(variantNode);
    center.appendChild(statusNode);

    const back = el('div', { class: 'button-row back-button-row' }, [
      createCrestButton({
        label: t('common.back'),
        small: true,
        onClick: () => {
          ctx.cancelRoom();
          ctx.navigate('play');
        },
      }),
    ]);

    body.appendChild(center);
    body.appendChild(back);
    root.appendChild(frame);
  }

  return {
    mount(rootEl, ctx) {
      root = rootEl;
      created = false;
      unsubRoom = ctx.roomClient.onUpdate(() => paint(ctx));
      paint(ctx);
    },
    update(ctx) {
      paint(ctx);
    },
    unmount() {
      unsubRoom?.();
      unsubRoom = null;
      frame?.remove();
      frame = null;
      root = null;
      created = false;
    },
  };
}
